import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/AdminUserPairsModal.css";
import { getAdminUserPairsIntelligence } from "../services/api";

const formatScore = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return `${Math.round(numeric)}`;
};

const formatMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "$0.00";
  return `${numeric >= 0 ? "+" : ""}$${numeric.toFixed(2)}`;
};

const marketLabel = (value) =>
  String(value || "").toUpperCase() === "FUTURES" ? "Futuros" : "Spot";

const AdminUserPairsModal = ({
  open,
  user,
  onClose,
  onApplyReplacement,
  onApplyAddition,
  onRemovePair,
  onApplyAllSuggestions,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pairIntelligence, setPairIntelligence] = useState(null);
  const [actionLoadingKey, setActionLoadingKey] = useState("");
  const [showApplyPreview, setShowApplyPreview] = useState(false);

  const loadPairIntelligence = async (keepCurrent = false) => {
    if (!open || !user?.username) return;
    setLoading(true);
    setError("");
    if (!keepCurrent) {
      setPairIntelligence(null);
    }
    const result = await getAdminUserPairsIntelligence(user.username);
    if (result.success) {
      setPairIntelligence(result.data?.pair_intelligence || null);
    } else {
      if (!keepCurrent) {
        setPairIntelligence(null);
      }
      setError(result.error || "No se pudo cargar el análisis de pares.");
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open || !user?.username) return;
      const result = await getAdminUserPairsIntelligence(user.username);
      if (cancelled) return;
      if (result.success) {
        setPairIntelligence(result.data?.pair_intelligence || null);
      } else {
        setPairIntelligence(null);
        setError(result.error || "No se pudo cargar el análisis de pares.");
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, user?.username]);

  useEffect(() => {
    if (!open) {
      setShowApplyPreview(false);
    }
  }, [open]);

  if (!open || !user) return null;

  const resolvedIntelligence = pairIntelligence || {};
  const showLoadingState = loading || (!pairIntelligence && !error);
  const rankedPairs = Array.isArray(resolvedIntelligence?.ranked_pairs)
    ? resolvedIntelligence.ranked_pairs
    : [];
  const addSuggestions = Array.isArray(resolvedIntelligence?.suggestions?.add)
    ? resolvedIntelligence.suggestions.add
    : [];
  const replaceSuggestions = Array.isArray(resolvedIntelligence?.suggestions?.replace)
    ? resolvedIntelligence.suggestions.replace
    : [];
  const hasSuggestions = addSuggestions.length > 0 || replaceSuggestions.length > 0;
  const replacementTargetSymbols = new Set(
    replaceSuggestions
      .map((item) => String(item?.add_symbol || "").toUpperCase())
      .filter(Boolean)
  );
  const previewAddSuggestions = addSuggestions.filter(
    (item) => !replacementTargetSymbols.has(String(item?.symbol || "").toUpperCase())
  );

  const handleApplyAllSuggestions = async () => {
    if (!onApplyAllSuggestions || !user) return;
    setActionLoadingKey("apply-all");
    const ok = await onApplyAllSuggestions(user);
    setActionLoadingKey("");
    if (ok) {
      setShowApplyPreview(false);
      await loadPairIntelligence(true);
    }
  };

  const handleApplyReplacement = async (item, index) => {
    if (!onApplyReplacement || !user) return;
    const key = `replace-${index}-${item?.remove_symbol}-${item?.add_symbol}`;
    setActionLoadingKey(key);
    const ok = await onApplyReplacement(user, item);
    setActionLoadingKey("");
    if (ok) {
      await loadPairIntelligence(true);
    }
  };

  const handleApplyAddition = async (item, index) => {
    if (!onApplyAddition || !user) return;
    const key = `add-${index}-${item?.symbol}`;
    setActionLoadingKey(key);
    const ok = await onApplyAddition(user, item);
    setActionLoadingKey("");
    if (ok) {
      await loadPairIntelligence(true);
    }
  };

  const handleRemovePair = async (pair, index) => {
    if (!onRemovePair || !user) return;
    const key = `remove-${index}-${pair?.symbol}-${pair?.market_type}`;
    setActionLoadingKey(key);
    const ok = await onRemovePair(user, pair, rankedPairs.length);
    setActionLoadingKey("");
    if (ok) {
      await loadPairIntelligence(true);
    }
  };

  return createPortal((
    <div className="admin-pairs-backdrop" onClick={() => onClose?.()}>
      <div className="admin-pairs-modal" onClick={(event) => event.stopPropagation()}>
        <header className="admin-pairs-header">
          <div>
            <h3>Pares configurados</h3>
            <p>{user?.username || "Usuario"} · {rankedPairs.length} pares en análisis</p>
          </div>
          <button type="button" className="admin-pairs-close" onClick={() => onClose?.()}>
            ✕
          </button>
        </header>

        <div className="admin-pairs-summary">
          <div className="admin-pairs-summary-chip">
            <span>Mejor lectura</span>
            <strong>{resolvedIntelligence?.top_summary || "Sin datos"}</strong>
          </div>
          <div className="admin-pairs-summary-note">
            {resolvedIntelligence?.market_note || "Sin sugerencias de mercado todavía."}
          </div>
          {hasSuggestions ? (
            <div className="admin-pairs-summary-actions">
              <button
                type="button"
                className="history-action-btn admin-pairs-apply-all-btn"
                onClick={() => setShowApplyPreview((prev) => !prev)}
                disabled={Boolean(actionLoadingKey)}
              >
                {showApplyPreview ? "Ocultar vista previa" : "Aplicar sugerencias"}
              </button>
            </div>
          ) : null}
          {showApplyPreview ? (
            <div className="admin-pairs-preview">
              <div className="admin-pairs-preview-title">Vista previa de cambios</div>
              {replaceSuggestions.length ? (
                <div className="admin-pairs-preview-block">
                  <h4>Sustituciones</h4>
                  <ul>
                    {replaceSuggestions.map((item, index) => (
                      <li key={`preview-replace-${item.remove_symbol}-${item.add_symbol}-${index}`}>
                        <strong>{item.remove_symbol}</strong>
                        <span> → </span>
                        <strong>{item.add_symbol}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {previewAddSuggestions.length ? (
                <div className="admin-pairs-preview-block">
                  <h4>Agregar</h4>
                  <ul>
                    {previewAddSuggestions.map((item, index) => (
                      <li key={`preview-add-${item.symbol}-${index}`}>
                        <strong>{item.symbol}</strong>
                        <span> · {formatScore(item.score)}/100</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {!replaceSuggestions.length && !previewAddSuggestions.length ? (
                <div className="admin-pairs-empty">No hay cambios netos para aplicar ahora mismo.</div>
              ) : (
                <div className="admin-pairs-preview-actions">
                  <button
                    type="button"
                    className="history-action-btn admin-pairs-preview-cancel"
                    onClick={() => setShowApplyPreview(false)}
                    disabled={Boolean(actionLoadingKey)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="history-action-btn admin-pairs-apply-all-btn"
                    onClick={handleApplyAllSuggestions}
                    disabled={Boolean(actionLoadingKey)}
                  >
                    {actionLoadingKey === "apply-all" ? "Aplicando sugerencias..." : "Confirmar aplicación"}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {error ? <div className="admin-pairs-alert">{error}</div> : null}

        <section className="admin-pairs-section">
          <div className="admin-pairs-section-title">Ranking actual</div>
          <div className="admin-pairs-grid">
            {showLoadingState ? (
              <div className="admin-pairs-empty">Cargando lectura de pares...</div>
            ) : rankedPairs.length === 0 ? (
              <div className="admin-pairs-empty">No hay pares configurados aún.</div>
            ) : (
              rankedPairs.map((pair, index) => (
                <article key={`${pair.symbol}-${index}`} className="admin-pairs-card">
                  <div className="admin-pairs-card-top">
                    <div>
                      <div className="admin-pairs-symbol-row">
                        <span className="admin-pairs-rank">#{index + 1}</span>
                        <strong>{pair.symbol}</strong>
                      </div>
                      <div className="admin-pairs-market">{marketLabel(pair.market_type)} · {pair.status_label || "Sin historial"}</div>
                    </div>
                    <span className={`history-reason-badge tone-${pair.status_tone || "muted"}`}>
                      {formatMoney(pair.net_pnl)}
                    </span>
                  </div>

                  <div className="admin-pairs-metrics">
                    <div>
                      <span>Órdenes abiertas históricas</span>
                      <strong>{pair.opened_orders || 0}</strong>
                    </div>
                    <div>
                      <span>Cerradas</span>
                      <strong>{pair.closed_orders || 0}</strong>
                    </div>
                    <div>
                      <span>Winrate</span>
                      <strong>{Number(pair.win_rate || 0).toFixed(1)}%</strong>
                    </div>
                  </div>

                  <p className="admin-pairs-context">{pair.context || "Sin contexto disponible."}</p>
                  <div className="admin-pairs-card-actions">
                    <button
                      type="button"
                      className="history-action-btn admin-pairs-remove-btn"
                      onClick={() => handleRemovePair(pair, index)}
                      disabled={Boolean(actionLoadingKey)}
                    >
                      {actionLoadingKey === `remove-${index}-${pair?.symbol}-${pair?.market_type}`
                        ? "Quitando..."
                        : "Eliminar par"}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <section className="admin-pairs-section">
          <div className="admin-pairs-section-title">Sugerencias del momento</div>
          <div className="admin-pairs-suggestions">
            <div className="admin-pairs-suggestion-block">
              <h4>Agregar</h4>
              {showLoadingState ? (
                <div className="admin-pairs-empty">Calculando sugerencias...</div>
              ) : addSuggestions.length === 0 ? (
                <div className="admin-pairs-empty">No hay pares más fuertes para sumar en este momento.</div>
              ) : (
                addSuggestions.map((item, index) => (
                  <div key={`${item.symbol}-${index}`} className="admin-pairs-suggestion-row">
                    <div>
                      <strong>{item.symbol}</strong>
                      <span>{marketLabel(item.market_type)} · {item.trend || "Sin tendencia"}</span>
                    </div>
                    <div className="admin-pairs-suggestion-score">
                      <span className={`history-reason-badge tone-${item.status_tone || "muted"}`}>
                        {formatScore(item.score)}/100
                      </span>
                    </div>
                    <p>{item.context}</p>
                    <button
                      type="button"
                      className="history-action-btn admin-pairs-apply-btn"
                      onClick={() => handleApplyAddition(item, index)}
                      disabled={Boolean(actionLoadingKey)}
                    >
                      {actionLoadingKey === `add-${index}-${item?.symbol}` ? "Agregando..." : "Agregar"}
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="admin-pairs-suggestion-block">
              <h4>Sustituir</h4>
              {showLoadingState ? (
                <div className="admin-pairs-empty">Calculando sugerencias...</div>
              ) : replaceSuggestions.length === 0 ? (
                <div className="admin-pairs-empty">Todavía no hay reemplazos claros frente a tus pares actuales.</div>
              ) : (
                replaceSuggestions.map((item, index) => (
                  <div key={`${item.remove_symbol}-${item.add_symbol}-${index}`} className="admin-pairs-suggestion-row">
                    <div>
                      <strong>{item.remove_symbol} → {item.add_symbol}</strong>
                      <span>
                        Sale {formatMoney(item.remove_score)} · entra {Math.round(Number(item.add_score || 0))}/100
                      </span>
                    </div>
                    <p>{item.reason}</p>
                    <button
                      type="button"
                      className="history-action-btn admin-pairs-apply-btn"
                      onClick={() => handleApplyReplacement(item, index)}
                      disabled={Boolean(actionLoadingKey)}
                    >
                      {actionLoadingKey === `replace-${index}-${item?.remove_symbol}-${item?.add_symbol}` ? "Aplicando..." : "Sustituir"}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  ), document.body);
};

export default React.memo(AdminUserPairsModal);
